import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft, Save, Loader2, Eye, FileText, Tag as TagIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { ImageUploader } from '../components/ImageUploader';

const apiBase = () => (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const slugify = (s) => (s || '')
  .toLowerCase()
  .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 80);

const SAMPLE_BODY = `## Einleitung

Schreibe hier den ersten Absatz Deines Artikels. **Fett** und *kursiv* sind möglich.

## Hauptteil

- Aufzählung Punkt 1
- Aufzählung Punkt 2
- Aufzählung Punkt 3

Schreibe weitere Absätze. Verlinke zu [unserer Webseite](https://loewenherz.ag).

## Fazit

Schlussfolgerung des Beitrags.`;

export const AdminBlogEditor = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [form, setForm] = useState({
    title: '',
    slug: '',
    excerpt: '',
    body: SAMPLE_BODY,
    coverImageUrl: '',
    tags: '',
    metaTitle: '',
    metaDescription: '',
    status: 'draft',
  });
  const [tab, setTab] = useState('edit'); // edit | preview
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [autoSlug, setAutoSlug] = useState(!isEdit);

  const loadPost = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    try {
      const r = await fetch(apiBase() + `/api/admin/blog/${id}`, { credentials: 'include' });
      if (!r.ok) throw new Error('Beitrag nicht gefunden');
      const { post } = await r.json();
      setForm({
        title: post.title || '',
        slug: post.slug || '',
        excerpt: post.excerpt || '',
        body: post.body || '',
        coverImageUrl: post.coverImageUrl || '',
        tags: (post.tags || []).join(', '),
        metaTitle: post.metaTitle || '',
        metaDescription: post.metaDescription || '',
        status: post.status || 'draft',
      });
      setAutoSlug(false);
    } catch (e) {
      toast.error(e.message);
      navigate('/admin/blog');
    } finally {
      setLoading(false);
    }
  }, [id, isEdit, navigate]);

  useEffect(() => {
    if (!user || !['admin', 'editor'].includes(user.role)) {
      navigate('/admin/login');
      return;
    }
    loadPost();
  }, [user, navigate, loadPost]);

  // Auto-slug from title until user manually edits slug
  useEffect(() => {
    if (autoSlug) {
      setForm((f) => ({ ...f, slug: slugify(f.title) }));
    }
  }, [form.title, autoSlug]);

  const save = async (asPublished) => {
    if (!form.title.trim()) { toast.error('Titel erforderlich'); return; }
    if (!form.body.trim())  { toast.error('Inhalt erforderlich'); return; }

    setBusy(true);
    try {
      const payload = {
        title: form.title.trim(),
        slug: form.slug.trim() || slugify(form.title),
        excerpt: form.excerpt.trim(),
        body: form.body,
        coverImageUrl: form.coverImageUrl.trim(),
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
        metaTitle: form.metaTitle.trim(),
        metaDescription: form.metaDescription.trim(),
        status: asPublished ? 'published' : (form.status || 'draft'),
      };
      const url = isEdit
        ? apiBase() + `/api/admin/blog/${id}`
        : apiBase() + '/api/admin/blog';
      const r = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || 'Speichern fehlgeschlagen');
      toast.success(asPublished ? 'Beitrag veröffentlicht' : 'Beitrag gespeichert');
      navigate('/admin/blog');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#C4A55A]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-[#C4A55A]/20 bg-black/80 backdrop-blur-md sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center gap-3">
          <Link to="/admin/blog" className="text-gray-400 hover:text-[#C4A55A]" data-testid="blog-editor-back">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <FileText className="h-6 w-6 text-[#C4A55A]" />
          <h1 className="text-xl font-semibold">{isEdit ? 'Beitrag bearbeiten' : 'Neuer Beitrag'}</h1>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => save(false)}
              disabled={busy}
              data-testid="blog-save-draft"
              className="px-3 py-1.5 rounded-lg border border-white/20 text-gray-200 hover:bg-white/10 text-sm inline-flex items-center gap-2"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Als Entwurf speichern
            </button>
            <button
              onClick={() => save(true)}
              disabled={busy}
              data-testid="blog-save-publish"
              className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#C4A55A] to-[#B8974E] text-black font-semibold text-sm inline-flex items-center gap-2"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Veröffentlichen
            </button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-6xl space-y-6">
        {/* Metadata */}
        <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl p-6 space-y-4">
          <Field label="Titel *">
            <input
              type="text" value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="z.B. Warum Schweizer Immobilien-AGs eine sichere Wahl sind"
              data-testid="blog-field-title"
              className="w-full bg-black/60 border border-white/15 rounded-lg px-3 py-2 text-white focus:border-[#C4A55A] focus:outline-none"
            />
          </Field>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Slug (URL)" hint="Wird automatisch aus dem Titel erzeugt.">
              <input
                type="text" value={form.slug}
                onChange={(e) => { setAutoSlug(false); setForm({ ...form, slug: slugify(e.target.value) }); }}
                placeholder="warum-schweizer-immobilien-ags"
                data-testid="blog-field-slug"
                className="w-full bg-black/60 border border-white/15 rounded-lg px-3 py-2 text-white font-mono text-sm focus:border-[#C4A55A] focus:outline-none"
              />
            </Field>
            <Field label="Cover-Bild" hint="Bild hochladen oder externen Link einfügen.">
              <ImageUploader
                label=""
                value={form.coverImageUrl}
                onChange={(url) => setForm({ ...form, coverImageUrl: url })}
                testId="blog-cover"
              />
            </Field>
          </div>

          <Field label="Kurzbeschreibung (Excerpt)" hint="2-3 Sätze, erscheint in der Blog-Übersicht.">
            <textarea
              rows={2} value={form.excerpt}
              onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              data-testid="blog-field-excerpt"
              className="w-full bg-black/60 border border-white/15 rounded-lg px-3 py-2 text-white focus:border-[#C4A55A] focus:outline-none"
            />
          </Field>

          <Field label="Tags" hint={'Komma-getrennt, max. 10 (z.B. „Immobilien, Aktien, Schweiz")'}>
            <div className="flex items-center gap-2">
              <TagIcon className="h-4 w-4 text-[#C4A55A]" />
              <input
                type="text" value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
                placeholder="Immobilien, Aktien, Schweiz"
                data-testid="blog-field-tags"
                className="flex-1 bg-black/60 border border-white/15 rounded-lg px-3 py-2 text-white focus:border-[#C4A55A] focus:outline-none"
              />
            </div>
          </Field>

          {/* SEO meta */}
          <details className="bg-black/40 rounded-lg p-3 text-sm">
            <summary className="cursor-pointer text-[#C4A55A] font-medium">SEO-Einstellungen (optional)</summary>
            <div className="mt-3 grid md:grid-cols-2 gap-4">
              <Field label="Meta-Titel" hint="Max. 60 Zeichen, sonst wird automatisch der Titel verwendet.">
                <input
                  type="text" value={form.metaTitle} maxLength={80}
                  onChange={(e) => setForm({ ...form, metaTitle: e.target.value })}
                  data-testid="blog-field-meta-title"
                  className="w-full bg-black/60 border border-white/15 rounded-lg px-3 py-2 text-white focus:border-[#C4A55A] focus:outline-none"
                />
              </Field>
              <Field label="Meta-Beschreibung" hint="Max. 160 Zeichen, sonst wird automatisch das Excerpt verwendet.">
                <input
                  type="text" value={form.metaDescription} maxLength={200}
                  onChange={(e) => setForm({ ...form, metaDescription: e.target.value })}
                  data-testid="blog-field-meta-desc"
                  className="w-full bg-black/60 border border-white/15 rounded-lg px-3 py-2 text-white focus:border-[#C4A55A] focus:outline-none"
                />
              </Field>
            </div>
          </details>
        </div>

        {/* Body editor + preview */}
        <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl">
          <div className="flex items-center gap-1 px-4 pt-4">
            <TabBtn active={tab === 'edit'} onClick={() => setTab('edit')} testId="blog-tab-edit">Bearbeiten</TabBtn>
            <TabBtn active={tab === 'preview'} onClick={() => setTab('preview')} testId="blog-tab-preview">
              <Eye className="h-3.5 w-3.5 inline mr-1" /> Vorschau
            </TabBtn>
          </div>

          {tab === 'edit' ? (
            <div className="p-4">
              <p className="text-xs text-gray-500 mb-2">
                <strong className="text-[#C4A55A]">Markdown-Editor</strong> – z.B. <code className="text-gray-400">## Titel</code>, <code className="text-gray-400">**fett**</code>, <code className="text-gray-400">*kursiv*</code>, <code className="text-gray-400">- Liste</code>, <code className="text-gray-400">[Text](url)</code>.
              </p>
              <textarea
                rows={22} value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                data-testid="blog-field-body"
                className="w-full bg-black/60 border border-white/15 rounded-lg px-3 py-3 text-white font-mono text-sm focus:border-[#C4A55A] focus:outline-none"
              />
            </div>
          ) : (
            <div className="p-6">
              {form.coverImageUrl && (
                <img src={form.coverImageUrl} alt="Cover" className="w-full max-h-72 object-cover rounded-lg mb-4 border border-white/10" />
              )}
              <h1 className="text-3xl font-bold mb-2">{form.title || 'Vorschau-Titel'}</h1>
              {form.excerpt && <p className="text-gray-400 italic mb-6">{form.excerpt}</p>}
              <BlogMarkdown body={form.body} />
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-wider text-gray-400 mb-1">{label}</span>
    {children}
    {hint && <span className="text-[10px] text-gray-500 mt-1 inline-block">{hint}</span>}
  </label>
);

const TabBtn = ({ active, children, onClick, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`px-3 py-1.5 rounded-t-lg text-sm border-t border-l border-r border-white/10 ${
      active ? 'bg-black text-[#C4A55A] border-[#C4A55A]/30' : 'bg-transparent text-gray-400 hover:text-white border-transparent'
    }`}
  >
    {children}
  </button>
);

// Reusable Markdown renderer used by Editor preview and BlogDetail
export const BlogMarkdown = ({ body }) => (
  <div className="prose prose-invert max-w-none
                  prose-headings:text-white prose-headings:font-bold
                  prose-h1:text-3xl prose-h2:text-2xl prose-h3:text-xl
                  prose-p:text-gray-300 prose-p:leading-relaxed
                  prose-a:text-[#C4A55A] prose-a:no-underline hover:prose-a:underline
                  prose-strong:text-white prose-em:text-gray-200
                  prose-li:text-gray-300 prose-blockquote:text-gray-400
                  prose-blockquote:border-l-[#C4A55A] prose-code:text-[#C4A55A]
                  prose-img:rounded-lg prose-img:border prose-img:border-white/10">
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{body || ''}</ReactMarkdown>
  </div>
);
